/**
 * Pool form guards — shared, pure logic for the two pool create/edit surfaces (the `PoolForm`
 * accordion form and the `PunchupPoolDetails` sub-page). Keeping the flow-tightening rules in one
 * place means both forms enforce them identically.
 *
 * See docs/pools_business_logic.md (rules T-2 / T-3): under Lifetime-Σ semantics a pool can only
 * coherently cap tickets that have NOT started selling, so a selling type can neither join a pool
 * nor be removed from one, and a pool's total can never drop below what its members already sold.
 */

/** The shape the pool forms iterate from the raw `events/:id` ticket-types payload. The guard
 *  functions read only `ID` / `sold` / `tickets_pool_id`; `name` is here for the checkbox label. */
export interface PoolMemberTicketType {
	ID: number;
	name: string;
	sold?: number;
	tickets_pool_id?: number | string | null;
}

/**
 * Lifetime-Σ floor for a pool's `total_amount`: the sum of `sold` across the currently selected
 * member types. A pool can never be smaller than the tickets its members have already sold. (T-3)
 */
export function getPoolSoldFloor(selectedIds: string[], ticketTypes: PoolMemberTicketType[]): number {
	const selected = new Set(selectedIds);
	return ticketTypes.reduce((sum, tt) => (selected.has(String(tt.ID)) ? sum + (tt.sold ?? 0) : sum), 0);
}

/**
 * Why a member checkbox is locked (rendered disabled). `null` → freely toggleable.
 *  - `attached-elsewhere`: already a member of a different pool (one type → at most one pool).
 *  - `selling-locked`:     a member of THIS pool that has already sold — can't be removed (T-3).
 *  - `selling-add`:        not in this pool and already selling — can't be added (T-2).
 */
export type PoolMemberLockReason = 'attached-elsewhere' | 'selling-locked' | 'selling-add' | null;

export function getPoolMemberLock(
	tt: PoolMemberTicketType,
	isSelected: boolean,
	poolId: string | null
): PoolMemberLockReason {
	const attachedElsewhere = !!tt.tickets_pool_id && (!poolId || String(tt.tickets_pool_id) !== poolId);
	if (attachedElsewhere) return 'attached-elsewhere';
	if ((tt.sold ?? 0) > 0) return isSelected ? 'selling-locked' : 'selling-add';
	return null;
}

/** True for a pool dropdown value that means "no pool" (unset, 0, or a non-positive number). */
export function isNoPool(poolId: number | string | undefined | null): boolean {
	return isNaN(Number(poolId)) || Number(poolId) <= 0;
}

/**
 * Whether to warn on save of a BRAND-NEW ticket type that joins no pool while the event already has
 * pools — the moment to make the one-way door explicit, because pool membership locks once a tier
 * starts selling (docs/pools_business_logic.md, T-5).
 */
export function shouldWarnNoPool(
	isNewTicket: boolean,
	poolCount: number,
	poolId: number | string | undefined
): boolean {
	return isNewTicket && poolCount > 0 && isNoPool(poolId);
}
